import React, { useState } from 'react';
import { useVacation } from '../../contexts/VacationContext';
import VacationModal from './VacationModal';
import '../../styles/vacation-modal.css';

/**
 * VacationDetailsModal Component
 * 
 * Displays vacation details with options to edit or delete. Shows staff, department, date, hours, and metadata. Includes delete confirmation dialog.
 */
const VacationDetailsModal = ({ isOpen, onClose, vacation }) => {
  const { deleteVacation, loading } = useVacation();
  
  const [showEditModal, setShowEditModal] = useState(false);
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);
  const [isDeleting, setIsDeleting] = useState(false);
  const [error, setError] = useState(null);

  if (!isOpen || !vacation) return null;

  /**
   * Handle edit button click
   */
  const handleEdit = () => {
    setShowEditModal(true);
  };

  /**
   * Handle edit modal close
   */
  const handleEditClose = () => {
    setShowEditModal(false);
    onClose(); // Close details modal too after edit
  };

  /**
   * Handle delete button click
   */
  const handleDelete = () => {
    setShowDeleteConfirm(true);
  };

  /**
   * Confirm and execute delete
   */
  const handleConfirmDelete = async () => {
    setIsDeleting(true);
    setError(null);

    try {
      await deleteVacation(vacation.id);
      setShowDeleteConfirm(false);
      onClose();
    } catch (err) {
      console.error('Error deleting vacation:', err);
      setError(err.message || 'Fehler beim Löschen des Urlaubs');
    } finally {
      setIsDeleting(false);
    }
  };

  /**
   * Cancel delete
   */
  const handleCancelDelete = () => {
    setShowDeleteConfirm(false);
  };

  /**
   * Handle close
   */
  const handleClose = () => {
    if (isDeleting) return;
    onClose();
  };

  /**
   * Handle backdrop click
   */
  const handleBackdropClick = (e) => {
    if (e.target === e.currentTarget) {
      handleClose();
    }
  };

  /**
   * Format date with day name
   */
  const formatDate = (date) => {
    let dateObj;
    
    // Handle different date formats
    if (typeof date === 'string') {
      // Date is a string in ISO format "YYYY-MM-DD"
      const [year, month, day] = date.split('-').map(Number);
      dateObj = new Date(year, month - 1, day);
    } else if (date?.toDate) {
      // Date is a Firestore Timestamp
      dateObj = date.toDate();
    } else if (date instanceof Date) {
      // Already a Date object
      dateObj = date;
    } else {
      return '-';
    }
    
    return dateObj.toLocaleDateString('de-DE', {
      weekday: 'long',
      day: '2-digit',
      month: 'long',
      year: 'numeric'
    });
  };

  /**
   * Format timestamp
   */
  const formatTimestamp = (date) => {
    if (!date) return '-';
    
    let dateObj;
    
    // Handle different date formats
    if (typeof date === 'string') {
      // Date is a string in ISO format
      dateObj = new Date(date);
    } else if (date?.toDate) {
      // Date is a Firestore Timestamp
      dateObj = date.toDate();
    } else if (date instanceof Date) {
      // Already a Date object
      dateObj = date;
    } else {
      return '-';
    }
    
    return dateObj.toLocaleDateString('de-DE', {
      day: '2-digit',
      month: '2-digit',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  return (
    <>
      <div className="vacation-modal-overlay" onClick={handleBackdropClick}>
        <div className="vacation-modal vacation-details-modal">
          {/* Header */}
          <div className="vacation-modal-header">
            <h2>Urlaubsdetails</h2>
            <button
              type="button"
              className="vacation-modal-close"
              onClick={handleClose}
              disabled={isDeleting}
              aria-label="Schließen"
            >
              ×
            </button>
          </div>

          {/* Content */}
          <div className="vacation-details-content">
            {/* Date */}
            <div className="detail-section">
              <div className="detail-label">Datum</div>
              <div className="detail-value detail-date">
                {formatDate(vacation.date)}
              </div>
            </div>

            {/* Staff */}
            <div className="detail-section">
              <div className="detail-label">Mitarbeiter</div>
              <div className="detail-value">
                {vacation.staffName || 'Gelöschter Mitarbeiter'}
              </div>
            </div>

            {/* Department */}
            <div className="detail-section">
              <div className="detail-label">Abteilung</div>
              <div className="detail-value">
                {vacation.departmentName || 'Unbekannte Abteilung'}
              </div>
            </div>

            {/* Hours */}
            <div className="detail-section">
              <div className="detail-label">Stunden</div>
              <div className={`detail-value ${vacation.hasNoShift() ? 'no-shift' : ''}`}>
                {vacation.hasNoShift() 
                  ? '0 Stunden (Kein Dienst geplant)'
                  : `${vacation.getDurationInHours().toFixed(1)} Stunden`
                }
              </div>
            </div>

            {/* Metadata */}
            <div className="detail-metadata">
              <div className="metadata-item">
                <span className="metadata-label">Erstellt am:</span>
                <span className="metadata-value">{formatTimestamp(vacation.createdAt)}</span>
              </div>
              {vacation.updatedAt && (
                <div className="metadata-item">
                  <span className="metadata-label">Aktualisiert am:</span>
                  <span className="metadata-value">{formatTimestamp(vacation.updatedAt)}</span>
                </div>
              )}
            </div>

            {/* Error Message */}
            {error && (
              <div className="error-banner">
                {error}
              </div>
            )}

            {/* Delete Confirmation */}
            {showDeleteConfirm && (
              <div className="delete-confirmation">
                <p className="confirm-message">
                  Möchten Sie diesen Urlaubstag wirklich löschen?
                </p>
                <div className="confirm-actions">
                  <button
                    type="button"
                    className="btn btn-secondary"
                    onClick={handleCancelDelete}
                    disabled={isDeleting}
                  >
                    Abbrechen
                  </button>
                  <button
                    type="button"
                    className="btn btn-danger"
                    onClick={handleConfirmDelete}
                    disabled={isDeleting}
                  >
                    {isDeleting ? 'Löschen...' : 'Löschen'}
                  </button>
                </div>
              </div>
            )}
          </div>

          {/* Actions */}
          {!showDeleteConfirm && (
            <div className="vacation-modal-actions">
              <button
                type="button"
                className="btn btn-secondary"
                onClick={handleClose}
                disabled={isDeleting}
              >
                Schließen
              </button>
              <button
                type="button"
                className="btn btn-outline"
                onClick={handleEdit}
                disabled={isDeleting || loading}
              >
                Bearbeiten
              </button>
              <button
                type="button"
                className="btn btn-danger"
                onClick={handleDelete}
                disabled={isDeleting || loading}
              >
                Löschen
              </button>
            </div>
          )}
        </div>
      </div>

      {/* Edit Modal */}
      {showEditModal && (
        <VacationModal
          isOpen={showEditModal}
          onClose={handleEditClose}
          mode="edit"
          vacationData={vacation}
        />
      )}
    </>
  );
};

export default VacationDetailsModal;